import { useState } from 'react';

import { Button } from '../../../components/ui/button';

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel: string;
  requiredConfirmation?: string;
  isDestructive?: boolean;
  isBusy?: boolean;
  onConfirm: (confirmationText?: string) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  requiredConfirmation,
  isDestructive = false,
  isBusy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [confirmationText, setConfirmationText] = useState('');
  const confirmationMatches =
    !requiredConfirmation ||
    confirmationText.trim() === requiredConfirmation.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-2xl border border-neutral-800 bg-neutral-900 p-6 shadow-2xl">
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-400">
          {message}
        </p>
        {requiredConfirmation && (
          <label className="mt-5 block">
            <span className="text-sm font-medium text-neutral-200">
              Confirmation
            </span>
            <input
              type="text"
              aria-label="Confirmation"
              value={confirmationText}
              onInput={(event) =>
                setConfirmationText(event.currentTarget.value)
              }
              className="mt-2 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 font-mono text-sm text-white outline-none transition-colors placeholder:text-neutral-600 focus:border-orange-400"
            />
            <p className="mt-2 text-xs text-neutral-500">
              Type{' '}
              <code className="rounded bg-neutral-950 px-1.5 py-0.5 text-neutral-300">
                {requiredConfirmation}
              </code>
            </p>
          </label>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => onConfirm(confirmationText.trim())}
            disabled={isBusy || !confirmationMatches}
            className={
              isDestructive
                ? 'bg-orange-700 text-white hover:bg-orange-600'
                : 'bg-teal-600 text-white hover:bg-teal-500'
            }
          >
            {isBusy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
